/* eslint-disable max-len */
/* eslint-disable complexity */
import {action, flow, makeAutoObservable} from 'mobx';
import {isUndefined} from 'lodash';
import {formatBackendTime, getSubscriptionPlanText} from '@/utils';
import {
    cancelAutoRenewApi,
    createSubscriptionOrder,
    enableAutoRenewApi,
    fetchBillingOverview,
    fetchOrderRecords,
    fetchPointsPackages,
    fetchPointsRecords,
    fetchSubscriptionPlans,
    fetchWithholdingFee,
    submitEnterpriseContact,
    type BillingCycleType,
    type BillingEditionType,
    type IContactSubmitParams,
    type IOrderRecordItem,
    type IPointsPackageItem,
    type IPointsRecordItem,
    type ISubscriptionCycleOptionItem,
    type ISubscriptionPlanItem,
    type IWithholdingFeeData,
    type PointsRecordType,
    type WithholdingFeeAction
} from '../api/billing';

export interface IBillingOverviewView {
    planName?: string;
    expireText: string;
    subscriptionPoints: number;
    subscriptionRefreshTime: string;
    purchasedPoints: number;
    purchasedNextExpireAt: string;
    purchasedNextExpireRemain: number;
    giftedPoints: number;
    giftedNextExpireAt: string;
    giftedNextExpireRemain: number;
    subscriptionPlanText?: string;
    dailyCredit?: number;
    autoRenewStatus?: 'enabled' | 'enable_by_rule' | 'enable_by_order';
    showEnableAutoRenewButton?: boolean;
    enableAutoRenewButtonText?: string;
    enableAutoRenewGoodsId?: string;
    serviceId?: string;
    region?: string;
    serviceType?: string;
    /** 企业版主账号订阅资源点（可分配） */
    enterpriseAllocatableCredits?: number;
    /** 企业版主账号通用资源点余量（shared_resource_credits.remaining） */
    generalCredits?: number;
    generalNextExpireAt?: string;
    generalNextExpireRemain?: number;
    /** 企业版主账号结转资源点余量（other_credits.remaining） */
    carryoverCredits?: number;
    carryoverNextExpireAt?: string;
    carryoverNextExpireRemain?: number;
    /** 子用户：剩余可用资源点 */
    subUserCreditRemaining?: number;
    /** 子用户：分配额度；-1 表示无上限 */
    subUserCreditQuota?: number;
    /** 子用户：额度重置时间 */
    subUserCreditRefreshTime?: string;
    /** 子用户：母账号可用资源点余额 */
    subUserMotherAvailable?: number;
}

export interface IPointsRecordView {
    id: string;
    event: string;
    source: string;
    sourceText: string;
    time: string;
    expireAt: string;
    change: number;
}

export interface IOrderRecordView {
    id: string;
    orderNo: string;
    planName: string;
    operation: string;
    spec: string;
    period: string;
    amount: string;
    status: string;
    createdAt: string;
    expireAt: string;
}

export interface ISubscriptionPlanHighlightView {
    key: string;
    text: string;
    tag?: string;
}

export interface ISubscriptionPlanFeatureView {
    text: string;
    /** feature 文案尾部可点击的链接（如企业版「立即咨询」跳转官网产品页） */
    link?: string;
}

export interface ISubscriptionPlanView {
    id: string;
    name: string;
    planName: string;
    priceText: string;
    unitText: string;
    subtitle: string;
    buttonText: string | undefined;
    buttonDisabled?: boolean;
    highlights: ISubscriptionPlanHighlightView[];
    features: ISubscriptionPlanFeatureView[];
    oldPriceText?: string;
    promoTagText?: string;
    active?: boolean;
    activeTagText?: string;
    billingUrl?: string;
    showCancelButton?: boolean;
    cancelButtonText?: string;
    subscriptionExpireAt?: string;
    showEnableAutoRenewButton?: boolean;
    enableAutoRenewButtonText?: string;
    enableAutoRenewMode?: 'rule' | 'order';
    serviceId?: string;
    serviceType?: string;
    region?: string;
}

export interface ISubscriptionCycleOptionView {
    id: BillingCycleType;
    label: string;
    discount: string;
}

export interface IPointsPackagePlanView {
    id: string;
    points: number;
    validityDays: number;
    monthlyPrice: string;
    packagePrice: string;
}

export interface IWithholdingFeeView {
    estimatedCost: number;
    availableCredits: number;
    gap: number;
    userLevel: string;
    rechargeUrl: string;
    /** 实际消耗资源点 */
    consumedCredit?: number | null;
    /** 已退回资源点 */
    refundedCredit?: number | null;
    /** 过期流量包资源点 */
    voidCredit?: number | null;
    /** 跨日沉没资源点 */
    sunkCredit?: number | null;
    /** 冷冻资源点 */
    frozenCredits?: number | null;
    /** 轮次变化信息（启用新配置时返回） */
    iterationChange?: {
        current: number;
        next: number;
    } | null;
    /** 原始最大演化轮次 */
    prevMaxIterations?: number;
    /** 当前最大演化轮次 */
    currentMaxIterations?: number;
    /** 缺口资源点 */
    shortfallCredits?: number;
    /** 轮次差额资源点 */
    accumulationCredits?: number;
}

export interface IWithholdingFeeCheckResult {
    shouldShowInsufficientCredits: boolean;
    message?: string;
}

const defaultOverview: IBillingOverviewView = {
    planName: '',
    expireText: '',
    subscriptionPoints: 0,
    subscriptionRefreshTime: '',
    purchasedPoints: 0,
    purchasedNextExpireAt: '',
    purchasedNextExpireRemain: 0,
    giftedPoints: 0,
    giftedNextExpireAt: '',
    giftedNextExpireRemain: 0,
    dailyCredit: 0
};

export const defaultOptions = [
    {label: '百度伐谋·管理平台', value: 'Workspace'},
    {label: '百度伐谋·产业决策智能体', value: 'WebAgent'}
];

const defaultPointsRecords: IPointsRecordView[] = [];
const defaultOrderRecords: IOrderRecordView[] = [];
const defaultSubscriptionPlans: ISubscriptionPlanView[] = [];

function formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
}

function getDefaultDateRange(): [string, string] {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    return [formatDate(startDate), formatDate(endDate)];
}
const defaultSubscriptionCycleOptions: ISubscriptionCycleOptionView[] = [];
const defaultPointsPackages: IPointsPackagePlanView[] = [];
const defaultPointsSources: string[] = [];

const defaultWithholdingFee: IWithholdingFeeView = {
    estimatedCost: 0,
    availableCredits: 0,
    gap: 0,
    userLevel: '',
    rechargeUrl: '',
    consumedCredit: null,
    refundedCredit: null,
    voidCredit: null,
    sunkCredit: null,
    frozenCredits: null,
    iterationChange: null
};

function getPointsRecordChange(item: IPointsRecordItem): number {
    const cost = Math.abs(Number(item.cost ?? 0));

    if (item.direction === 'allocate') {
        return cost;
    }

    if (item.direction === 'reclaim') {
        return -cost;
    }

    return Number(item.cost ?? 0);
}

function mapPointsRecords(data: IPointsRecordItem[] | undefined): IPointsRecordView[] {
    return (data || []).map((item, index) => {
        const source = item.product || '-';
        const matchedSourceOption = defaultOptions.find(option => option.value === source);

        return {
            id: item.ticket || `record-${index}`,
            event: item.comment || '-',
            source,
            sourceText: matchedSourceOption?.label || source,
            time: item.time || '-',
            expireAt: item.expire_at || '',
            change: getPointsRecordChange(item)
        };
    });
}

function mapOrderRecords(data: IOrderRecordItem[] | undefined): IOrderRecordView[] {
    return (data || []).map((item, index) => ({
        ...item,
        id: item.order_id || `order-${index}`,
        orderNo: item.order_id || '-',
        planName: item.plan_display_name || item.operation || '-',
        operation: item.operation || item.plan_display_name || '-',
        spec: item.spec || '-',
        period: item.expire_at ? `${item.created_at || '-'} ~ ${item.expire_at}` : '-',
        amount: item.amount || '-',
        status: item.status || '-',
        createdAt: item.created_at || '-',
        expireAt: item.expire_at || '-'
    }));
}

function mapCycleOptions(data: ISubscriptionCycleOptionItem[] | undefined): ISubscriptionCycleOptionView[] {
    return (data || []).map(item => ({
        id: item.cycle,
        label: item.label,
        discount: item.discount || ''
    }));
}

function mapSubscriptionPlans(data: ISubscriptionPlanItem[] | undefined): ISubscriptionPlanView[] {
    return (data || []).map(item => ({
        id: item.goods_id || '',
        name: item.plan_display_name || '-',
        planName: item.plan_name || '',
        priceText: item.price || '-',
        unitText: item.cycle_display || '',
        subtitle: item.subtitle || '',
        buttonText: item.button_text,
        buttonDisabled: item.button_disabled,
        highlights: (item.highlights || []).map(highlight => ({
            key: highlight.key,
            text: highlight.text,
            tag: highlight.tag || undefined
        })),
        features: (item.features || []).map(feature => ({
            text: feature.text,
            link: feature.link || undefined
        })),
        oldPriceText: item.old_price_text,
        promoTagText: item.promo_tag_text,
        active: item.active,
        activeTagText: item.active_tag_text,
        billingUrl: item.billingURl,
        showCancelButton: item.show_cancel_button ?? false,
        cancelButtonText: item.cancel_button_text,
        subscriptionExpireAt: item.subscription_expire_at,
        showEnableAutoRenewButton: item.show_enable_auto_renew_button ?? false,
        enableAutoRenewButtonText: item.enable_auto_renew_button_text,
        enableAutoRenewMode: item.enable_auto_renew_mode,
        region: item.region,
        serviceId: item.service_id,
        serviceType: item.service_type
    }));
}

function mapPointsPackages(data: IPointsPackageItem[] | undefined): IPointsPackagePlanView[] {
    return (data || []).map((item, index) => ({
        id: item.goods_id || item.plan_name || `package-${index}`,
        points: Number(item.credits ?? 0),
        validityDays: item.validity_days,
        monthlyPrice: item.monthly_price,
        packagePrice: item.package_price
    }));
}

function mapWithholdingFee(data?: IWithholdingFeeData): IWithholdingFeeView {
    const estimatedCost = Number(data?.estimatedCost || 0);
    const availableCredits = Number(data?.availableCredits || 0);

    const iterationChange = data?.iteration_change
        ? {current: Number(data.iteration_change.current), next: Number(data.iteration_change.next)}
        : null;

    return {
        estimatedCost,
        availableCredits,
        gap: Math.max(estimatedCost - availableCredits, 0),
        userLevel: data?.userLevel || '',
        rechargeUrl: data?.rechargeUrl || '',
        consumedCredit: data?.consumedCredit ?? null,
        refundedCredit: data?.refundedCredit ?? null,
        voidCredit: data?.voidCredit ?? null,
        sunkCredit: data?.sunkCredit ?? null,
        frozenCredits: data?.frozenCredits ?? null,
        iterationChange,
        prevMaxIterations: data?.prevMaxIterations,
        currentMaxIterations: data?.currentMaxIterations,
        shortfallCredits: data?.shortfallCredits ?? 0,
        accumulationCredits: data?.accumulationCredits
    };
}

export class BillingStore {
    showSubscriptionModal = false;
    showPurchaseModal = false;
    showContactModal = false;

    pageLoading = false;
    pointsLoading = false;
    ordersLoading = false;
    overviewLoading = false;
    subscriptionPlansLoading = false;
    pointsPackagesLoading = false;
    subscriptionSubmitting = false;
    purchaseSubmitting = false;
    contactSubmitting = false;

    overview: IBillingOverviewView = defaultOverview;
    pointsRecords: IPointsRecordView[] = defaultPointsRecords;
    pointsSources: string[] = defaultPointsSources;
    pointsComments: string[] = [];
    pointsCommentOptions: string[] = [];
    pointsRecordType: PointsRecordType = 'income';
    pointsDateRange: [string, string] | null = getDefaultDateRange();
    pointsTotal = 0;
    pointsPage = 1;
    pointsPageSize = 50;
    orderRecords: IOrderRecordView[] = defaultOrderRecords;
    ordersTotal = 0;
    ordersPage = 1;
    ordersPageSize = 50;
    dailyCredit = 0;
    subscriptionPlans: ISubscriptionPlanView[] = defaultSubscriptionPlans;
    enterprisePlans: ISubscriptionPlanView[] = defaultSubscriptionPlans;
    enterprisePlansLoading = false;
    subscriptionCycleOptions: ISubscriptionCycleOptionView[] = defaultSubscriptionCycleOptions;
    pointsPackages: IPointsPackagePlanView[] = defaultPointsPackages;
    withholdingFee: IWithholdingFeeView = defaultWithholdingFee;

    constructor() {
        makeAutoObservable(this, {
            fetchPageInitialData: flow.bound,
            fetchOverview: flow.bound,
            fetchPointsRecords: flow.bound,
            fetchOrderRecords: flow.bound,
            fetchSubscriptionPlans: flow.bound,
            fetchEnterprisePlans: flow.bound,
            createSubscription: flow.bound,
            fetchPointsPackages: flow.bound,
            fetchWithholdingFee: flow.bound,
            createPointsPurchase: flow.bound,
            submitEnterpriseContact: flow.bound,
            cancelAutoRenew: flow.bound,
            openSubscriptionModal: action.bound,
            closeSubscriptionModal: action.bound,
            openPurchaseModal: action.bound,
            closePurchaseModal: action.bound,
            openContactModal: action.bound,
            closeContactModal: action.bound,
            openContactFromPurchase: action.bound,
            setContactSubmitting: action.bound,
            setPointsSources: action.bound,
            setPointsComments: action.bound,
            setPointsRecordType: action.bound,
            setPointsDateRange: action.bound,
            setPointsPage: action.bound,
            setOrdersPage: action.bound,
            enableAutoRenew: flow.bound
        });
    }

    openSubscriptionModal() {
        this.showSubscriptionModal = true;
    }

    closeSubscriptionModal() {
        this.showSubscriptionModal = false;
    }

    openPurchaseModal() {
        this.showPurchaseModal = true;
    }

    closePurchaseModal() {
        this.showPurchaseModal = false;
    }

    openContactModal() {
        this.showContactModal = true;
    }

    closeContactModal() {
        this.showContactModal = false;
    }

    openContactFromPurchase() {
        this.closePurchaseModal();
        this.openContactModal();
    }

    setContactSubmitting(submitting: boolean) {
        this.contactSubmitting = submitting;
    }

    setPointsSources(sources: string[]) {
        this.pointsSources = sources;
    }

    setPointsComments = (comments: string[]) => {
        this.pointsComments = comments;
    };

    setPointsRecordType = (recordType: PointsRecordType) => {
        this.pointsRecordType = recordType;
    };

    setPointsDateRange = (dateRange: [string, string] | null) => {
        this.pointsDateRange = dateRange;
    };

    setPointsPage(page: number) {
        this.pointsPage = page;
    }

    setOrdersPage(page: number) {
        this.ordersPage = page;
    }

    * fetchPageInitialData(): Generator<Promise<any>, void, any> {
        this.pageLoading = true;
        try {
            yield Promise.all([
                this.fetchOverview(),
                this.fetchPointsRecords({
                    page: this.pointsPage,
                    pageSize: this.pointsPageSize
                })
            ]);
        }
        finally {
            this.pageLoading = false;
        }
    }

    * fetchOverview(): Generator<Promise<any>, void, any> {
        this.overviewLoading = true;
        try {
            const res = yield fetchBillingOverview();
            const data = res.data;

            if (!data) {
                return;
            }

            this.overview = {
                subscriptionPlanText: getSubscriptionPlanText(data.subscription_plan),
                planName: data.subscription_plan || '',
                dailyCredit: data.daily_credit,
                expireText: data.subscription_expire_at ? formatBackendTime(data.subscription_expire_at) : '',
                subscriptionPoints: Number(data.enterprise_package_credits ?? data.subscription_credits ?? 0),
                subscriptionRefreshTime: data.refresh_time || '',
                purchasedPoints: Number(data.package_credits ?? 0),
                purchasedNextExpireAt: data.package_next_expire_at || '',
                purchasedNextExpireRemain: Number(data.package_next_expire_remain ?? 0),
                giftedPoints: Number(data.bonus_credits ?? 0),
                giftedNextExpireAt: data.bonus_next_expire_at || '',
                giftedNextExpireRemain: Number(data.bonus_next_expire_remain ?? 0),
                autoRenewStatus: data.auto_renew_status,
                showEnableAutoRenewButton: data.show_enable_auto_renew_button ?? false,
                enableAutoRenewButtonText: data.enable_auto_renew_button_text,
                enableAutoRenewGoodsId: data.enable_auto_renew_goods_id,
                serviceId: data.service_id,
                region: data.region,
                serviceType: data.service_type,
                // 企业母账号：订阅（可分配）取企业总池 enterprise_credits.remaining
                enterpriseAllocatableCredits: data.enterprise_pool?.enterprise_credits
                    ? Number(data.enterprise_pool.enterprise_credits.remaining ?? 0)
                    : (isUndefined(data.enterprise_package_credits) ? undefined : Number(data.enterprise_package_credits)),
                // 企业母账号：通用 = shared_resource_credits
                generalCredits: Number(data.enterprise_pool?.shared_resource_credits?.remaining ?? 0),
                generalNextExpireAt: data.enterprise_pool?.shared_resource_credits?.expire_at || '',
                generalNextExpireRemain: Number(data.enterprise_pool?.shared_resource_credits?.expire_remain ?? 0),
                // 企业母账号：结转 = other_credits
                carryoverCredits: Number(data.enterprise_pool?.other_credits?.remaining ?? 0),
                carryoverNextExpireAt: data.enterprise_pool?.other_credits?.expire_at || '',
                carryoverNextExpireRemain: Number(data.enterprise_pool?.other_credits?.expire_remain ?? 0),
                // 子账号额度：优先取 sub_user_credit 块，回退扁平字段
                subUserCreditRemaining: !isUndefined(data.sub_user_credit?.remaining)
                    ? Number(data.sub_user_credit?.remaining)
                    : (isUndefined(data.sub_user_credit_remaining) ? undefined : Number(data.sub_user_credit_remaining)),
                subUserCreditQuota: !isUndefined(data.sub_user_credit?.quota)
                    ? Number(data.sub_user_credit?.quota)
                    : (isUndefined(data.sub_user_credit_quota) ? undefined : Number(data.sub_user_credit_quota)),
                subUserCreditRefreshTime: data.sub_user_credit?.refresh_time || '',
                subUserMotherAvailable: isUndefined(data.sub_user_credit?.mother_available_credits)
                    ? undefined
                    : Number(data.sub_user_credit?.mother_available_credits)
            };
        }
        finally {
            this.overviewLoading = false;
        }
    }

    * fetchPointsRecords(params?: {
        sources?: string[];
        comments?: string[];
        ticket?: string;
        type?: PointsRecordType;
        dateRange?: [string, string] | null;
        page?: number;
        pageSize?: number;
    }): Generator<Promise<any>, void, any> {
        this.pointsLoading = true;
        // 清空旧数据，避免请求期间显示旧数据
        this.pointsRecords = [];
        try {
            const sources = params?.sources ?? this.pointsSources;
            const comments = params?.comments ?? this.pointsComments;
            const recordType = params?.type ?? this.pointsRecordType;
            const dateRange = params?.dateRange === undefined ? this.pointsDateRange : params.dateRange;
            const res = yield fetchPointsRecords({
                sources,
                comments,
                ticket: params?.ticket,
                type: recordType,
                start_date: dateRange?.[0],
                end_date: dateRange?.[1],
                page: params?.page,
                page_size: params?.pageSize
            });
            const nextRecords = mapPointsRecords(res.data?.data);

            this.pointsRecords = nextRecords;
            this.pointsCommentOptions = res.data?.comment_enums || [];
            this.pointsTotal = Number(res.data?.total ?? 0);
            this.pointsPage = Number(res.data?.page ?? params?.page ?? 1);
            this.pointsPageSize = Number(res.data?.page_size ?? params?.pageSize ?? 50);
        }
        finally {
            this.pointsLoading = false;
        }
    }

    * fetchOrderRecords(params?: {
        dateRange?: [string, string] | null;
        page?: number;
        pageSize?: number;
    }): Generator<Promise<any>, void, any> {
        this.ordersLoading = true;
        try {
            const dateRange = params?.dateRange === undefined ? this.pointsDateRange : params.dateRange;
            const res = yield fetchOrderRecords({
                start_date: dateRange?.[0],
                end_date: dateRange?.[1],
                page: params?.page,
                page_size: params?.pageSize
            });
            const nextRecords = mapOrderRecords(res.data?.data);

            this.orderRecords = nextRecords;
            this.ordersTotal = Number(res.data?.total ?? 0);
            this.ordersPage = Number(res.data?.page ?? params?.page ?? 1);
            this.ordersPageSize = Number(res.data?.page_size ?? params?.pageSize ?? 50);
        }
        finally {
            this.ordersLoading = false;
        }
    }

    get enableAutoRenewByRule() {
        return this.overview.autoRenewStatus === 'enable_by_rule';
    }

    shouldEnableAutoRenewByRule(plan: ISubscriptionPlanView) {
        return plan.enableAutoRenewMode === 'rule' || this.enableAutoRenewByRule;
    }

    * fetchSubscriptionPlans(edition: BillingEditionType, cycle: BillingCycleType) {
        this.subscriptionPlansLoading = true;
        try {
            const res: ApiResponse = yield fetchSubscriptionPlans({edition, cycle: edition === 'personal' ? cycle : undefined});
            const nextPlans = mapSubscriptionPlans((res.data)?.plans);
            const nextCycleOptions = mapCycleOptions((res.data)?.cycle_options);

            this.subscriptionPlans = nextPlans;
            this.subscriptionCycleOptions = nextCycleOptions;
        }
        finally {
            this.subscriptionPlansLoading = false;
        }
    }

    * fetchEnterprisePlans() {
        this.enterprisePlansLoading = true;
        try {
            const res: ApiResponse = yield fetchSubscriptionPlans({edition: 'enterprise'});
            this.enterprisePlans = mapSubscriptionPlans((res.data)?.plans);
        }
        finally {
            this.enterprisePlansLoading = false;
        }
    }

    * createSubscription(planId: string, cycle: BillingCycleType) {
        this.subscriptionSubmitting = true;
        try {
            const res: ApiResponse = yield createSubscriptionOrder({goods_id: planId, order_type: cycle});
            return res.data;
        }
        finally {
            this.subscriptionSubmitting = false;
        }
    }

    * fetchPointsPackages() {
        this.pointsPackagesLoading = true;
        try {
            const res: ApiResponse = yield fetchPointsPackages();
            const nextPlans = mapPointsPackages(res.data);

            this.pointsPackages = nextPlans;
        }
        finally {
            this.pointsPackagesLoading = false;
        }
    }

    * fetchWithholdingFee(
        params: {job_id: string, userId?: string, iteration?: number, action?: WithholdingFeeAction}
    ) {
        const actionType = params.action ? {action: params.action} : {};
        const iteration = isUndefined(params.iteration) ? {} : {iteration: params.iteration};
        const res: ApiResponse = yield fetchWithholdingFee({
            job_id: params.job_id,
            userId: params.userId,
            ...actionType,
            ...iteration
        });
        const nextWithholdingFee = mapWithholdingFee(res.data);
        const code = String(res.code);
        const gap = nextWithholdingFee.shortfallCredits ?? 0;

        this.withholdingFee = {
            ...nextWithholdingFee,
            gap
        };

        if (code === '0'
            && nextWithholdingFee.estimatedCost === nextWithholdingFee.availableCredits
            && !(nextWithholdingFee.estimatedCost > 0)
        ) {
            return;
        }

        if (code === '0' && gap <= 0) {
            return;
        }

        return {
            shouldShowInsufficientCredits: code === 'ErrInsufficientQuota'
                || gap > 0,
            message: res.message || ''
        };
    }

    * createPointsPurchase(goodsId: string, quantity: number) {
        this.purchaseSubmitting = true;
        try {
            const res: ApiResponse = yield createSubscriptionOrder({goods_id: goodsId, quantity});
            return res.data;
        }
        finally {
            this.purchaseSubmitting = false;
        }
    }

    * submitEnterpriseContact(params: IContactSubmitParams) {
        this.contactSubmitting = true;
        try {
            yield submitEnterpriseContact(params);
        }
        finally {
            this.contactSubmitting = false;
        }
    }

    * cancelAutoRenew(params: {
        serviceId: string;
        serviceType: string;
        region: string;
    }) {
        this.subscriptionPlansLoading = true;
        try {
            yield cancelAutoRenewApi(params);
            yield this.fetchOverview();
        }
        finally {
            this.subscriptionPlansLoading = false;
        }
    }

    * enableAutoRenew(params: {
        serviceId: string;
        serviceType: string;
        region: string;
    }) {
        this.subscriptionSubmitting = true;
        try {
            const res: ApiResponse = yield enableAutoRenewApi(params);
            yield this.fetchOverview();
            return res.data;
        }
        finally {
            this.subscriptionSubmitting = false;
        }
    }
}
